import { ActionsUnion, createAction } from '@housinganywhere/safe-redux';
import { Thunk } from 'ha/myredux/types';
import { UserInfoApiV2 } from 'ha/services/apiV2/types/UserInfo';
import { notEmpty } from 'ha/utils/notEmpty';

import {
  getFromStorage,
  setInStorage,
  removeFromStorage,
} from 'ha/utils/storage';

import {
  BILLIN_INFO_STORAGE_KEY,
  SHOW_WE_WANT_TO_PAY_STORAGE_KEY,
} from 'ha/modules/WeWantToPay/constants';
import { showWeWantToPay } from 'ha/modules/WeWantToPay/selectors';

import { FetchUserBillingInfoTypes } from 'ha/modules/AuthLogic/constants';

export const Actions = {
  start: () => createAction(FetchUserBillingInfoTypes.start),
  done: (data: UserInfoApiV2) =>
    createAction(FetchUserBillingInfoTypes.done, data),
  fail: (error: Error) => createAction(FetchUserBillingInfoTypes.fail, error),
};
export type Actions = ActionsUnion<typeof Actions>;

const fetchAndCacheBillingInfo: () => Thunk = () => (
  dispatch,
  getState,
  { apiV2 },
) => {
  dispatch(Actions.start());

  return apiV2
    .getUserInformation('me', {
      expand: [
        'hasCompleteBillingInformation',
        'savedPayoutMethodsCount',
        'advertiserBookingsCount',
      ].join(','),
    })
    .then(res => {
      dispatch(Actions.done(res.data));

      setInStorage(BILLIN_INFO_STORAGE_KEY, res.data);
      setInStorage(
        SHOW_WE_WANT_TO_PAY_STORAGE_KEY,
        showWeWantToPay(getState()),
      );
    })
    .catch(error => {
      dispatch(Actions.fail(error));

      removeFromStorage(BILLIN_INFO_STORAGE_KEY);
      removeFromStorage(SHOW_WE_WANT_TO_PAY_STORAGE_KEY);
    });
};

// Fetch user billing information and cache it.
// - If WeWantToPay should be displayed, fetch info from API (invalidating
//   cache is not possible when adding payout methods).
// - If WeWantToPay should NOT be displayed, use cache, if user doesn't have
//   bookings check that first to potentially invalidate cache.
// - Cache values are invalidated when adding/removing payout methods,
//   and when saving billing information.
//
// This is done to avoid calling API for data that need to be fetched from other
// microservice ('savedPayoutMethodsCountd' & 'hasCompleteBillingInformation').

const fetchUserBillingInfo: () => Thunk = () => (
  dispatch,
  getState,
  { apiV2 },
) => {
  const data: UserInfoApiV2 | undefined = getFromStorage(
    BILLIN_INFO_STORAGE_KEY,
  );
  const show: boolean | undefined = getFromStorage(
    SHOW_WE_WANT_TO_PAY_STORAGE_KEY,
  );
  const noCache = !notEmpty(data) || !notEmpty(show);

  if (noCache || show) {
    return dispatch(fetchAndCacheBillingInfo());
  }

  if (data && data.advertiserBookingsCount > 0) {
    dispatch(Actions.done(data));

    return Promise.resolve();
  }

  return apiV2
    .getUserInformation('me', { expand: 'advertiserBookingsCount' })
    .then(res => {
      if (data && res.data && res.data.advertiserBookingsCount === 0) {
        dispatch(Actions.done(data));

        return;
      }

      // eslint-disable-next-line consistent-return
      return dispatch(fetchAndCacheBillingInfo());
    })
    .catch(() => dispatch(fetchAndCacheBillingInfo()));
};

export default fetchUserBillingInfo;
